// @ts-check

/**
 * @file Theme-aware HayaSelect wrapper used by every screen-level select
 * so the chevron, dropdown surface, and option text colour all come from
 * the active palette rather than per-screen custom-style blocks.
 */

import Text from "@kaspernj/api-maker/build/utils/text"
import HayaSelect from "haya-select/build/select/index.js"
import _ from "gettext-universal/build/src/translate.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"

import {FORM_CONTROL_BORDER_RADIUS, FORM_CONTROL_BORDER_WIDTH, FORM_CONTROL_FONT_SIZE, FORM_CONTROL_LINE_HEIGHT, FORM_CONTROL_PADDING_HORIZONTAL, FORM_CONTROL_PADDING_VERTICAL} from "../shared/form-control-metrics"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {HayaSelectProps} from "haya-select/build/select/index.js" */
/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {TextInputProps} from "react-native" */
/** @type {Record<string, TextInputProps>} */
const searchTextInputProps = {}
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{text: string, value: string}} SelectOption */
/** @typedef {{page?: number, searchValue?: string, values?: (string | number)[]}} SelectOptionsLoaderArgs */
/** @typedef {{options: SelectOption[], page?: number, pageSize?: number, totalCount?: number}} SelectOptionsResult */
/** @typedef {(args?: SelectOptionsLoaderArgs) => Promise<SelectOption[] | SelectOptionsResult> | SelectOption[] | SelectOptionsResult} SelectOptionsLoader */

/**
 * @typedef {object} SelectProps
 * @property {boolean} [closeOnChange] Close the dropdown after every selection change.
 * @property {string | number} [defaultValue] Initial selected value for async options.
 * @property {(string | number)[]} [defaultValues] Initial selected values for async options.
 * @property {boolean} [multiple] Allow multiple selections.
 * @property {(...args: unknown[]) => unknown} [onChangeValue] Selection change handler.
 * @property {SelectOption[] | SelectOptionsLoader} options Available options.
 * @property {string} [placeholder] Placeholder for the empty state.
 * @property {boolean} [search] Whether the select supports searching.
 * @property {string} testID Stable test id forwarded to HayaSelect.
 * @property {(string | number)[]} [values] Selected values for controlled use.
 */

/** @typedef {Record<string, never>} SelectState */

/**
 * Shared theme-aware select. Renders HayaSelect with palette-driven
 * `selectContainer` / `optionsContainer` / placeholder colours so the
 * dropdown blends into both light and dark themes without per-screen
 * styling.
 * @augments {ShapeComponent<SelectProps, SelectState>}
 */
class Select extends ShapeComponent {
  static propTypes = propTypesExact({
    closeOnChange: PropTypes.bool,
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultValues: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    ),
    multiple: PropTypes.bool,
    onChangeValue: PropTypes.func,
    options: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired
        })
      ),
      PropTypes.func
    ]).isRequired,
    placeholder: PropTypes.string,
    search: PropTypes.bool,
    testID: PropTypes.string.isRequired,
    values: PropTypes.array
  })
  static defaultProps = {
    closeOnChange: false,
    defaultValue: undefined,
    defaultValues: undefined,
    multiple: false,
    onChangeValue: undefined,
    placeholder: undefined,
    search: false,
    values: undefined
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the themed HayaSelect.
   * @returns {ReactNode} Select content.
   */
  render() {
    const {closeOnChange, defaultValue, defaultValues, multiple, onChangeValue, options, placeholder, search, testID, values} = this.p
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    /** @type {HayaSelectProps["options"]} */
    // @ts-expect-error HayaSelect runtime supports async option loaders with search args.
    const hayaSelectOptions = options
    const hayaStyles = styles[`hayaSelect-${themeName}`] ||= {
      chevron: {
        color: colors.textMuted
      },
      chevronContainer: {
        height: undefined
      },
      currentOption: {
        marginRight: 6
      },
      currentOptionGroupText: {
        color: colors.text,
        fontSize: FORM_CONTROL_FONT_SIZE,
        fontWeight: 500,
        lineHeight: FORM_CONTROL_LINE_HEIGHT
      },
      currentOptionPresentationText: {
        color: colors.text,
        fontSize: FORM_CONTROL_FONT_SIZE,
        fontWeight: 500,
        lineHeight: FORM_CONTROL_LINE_HEIGHT
      },
      nothingSelected: {
        color: colors.placeholder,
        fontSize: FORM_CONTROL_FONT_SIZE,
        lineHeight: FORM_CONTROL_LINE_HEIGHT
      },
      option: {
        backgroundColor: colors.surface
      },
      optionPresentationText: {
        color: colors.text,
        fontSize: FORM_CONTROL_FONT_SIZE,
        fontWeight: 500,
        lineHeight: FORM_CONTROL_LINE_HEIGHT
      },
      optionsContainer: {
        backgroundColor: colors.surface,
        borderColor: colors.borderStrong,
        borderWidth: 1
      },
      searchTextInput: {
        color: colors.text
      },
      selectContainer: {
        alignItems: "center",
        backgroundColor: colors.surface,
        borderColor: colors.borderStrong,
        borderRadius: FORM_CONTROL_BORDER_RADIUS,
        borderWidth: FORM_CONTROL_BORDER_WIDTH,
        flexDirection: "row",
        paddingBottom: FORM_CONTROL_PADDING_VERTICAL,
        paddingLeft: FORM_CONTROL_PADDING_HORIZONTAL,
        paddingRight: FORM_CONTROL_PADDING_HORIZONTAL,
        paddingTop: FORM_CONTROL_PADDING_VERTICAL,
        width: "100%"
      }
    }
    return (
      <View testID={testID}>
        <HayaSelect
          closeOnChange={closeOnChange}
          defaultValue={defaultValue}
          defaultValues={defaultValues}
          multiple={multiple}
          noOptionsText={this.tt.renderNoOptions}
          onChangeValue={onChangeValue}
          options={hayaSelectOptions}
          optionsAbsolute
          optionsPortal
          placeholder={placeholder}
          search={search}
          searchTextInputProps={searchTextInputProps[`searchTextInput-${themeName}`] ||= {
            placeholderTextColor: colors.placeholder,
            selectionColor: colors.focusRing
          }}
          selectedBackgroundColor={colors.infoBackground}
          selectedHoverBackgroundColor={colors.chipActiveBackground}
          styles={hayaStyles}
          values={values}
        />
      </View>
    )
  }

  /**
   * Renders an explicit themed Text for HayaSelect's empty-results panel.
   * The `no-options-container` renders inside the dropdown portal, so
   * the default RN Text it builds wouldn't otherwise see the
   * `WithDefaultStyle.Text` slot.
   * @returns {ReactNode} Themed empty-state text.
   */
  renderNoOptions = () => {
    const colors = this.tt.colors
    return (
      <Text
        style={styles[`noOptions-${this.tt.themeName}`] ||= {
          color: colors.textMuted,
          paddingHorizontal: 12,
          paddingVertical: 10
        }}
      >
        {_("No options found")}
      </Text>
    )
  }
}

/** @type {NamedExoticComponent<SelectProps>} */
const SelectComponent = memo(shapeComponent(Select))

export default SelectComponent
